import {
  useAllInvestmentAccountsQuery,
  useInvestmentHoldingsQuery,
  useSetSecurityTreatAsCashMutation,
} from '@transactions/graphql';
import {parseDate, today} from 'common/date';
import {ChevronDown, ChevronRight, Coins, Search} from 'lucide-react';
import * as React from 'react';
import {Button} from 'src/Button';
import {FilterDropdown} from 'src/components/FilterDropdown';
import {
  accountKeyOf,
  assetsOf,
  filterHoldings,
  groupsByAccount,
  returnOf,
  rowOf,
  rowsBySecurity,
  sortRows,
  totalsByTreatment,
  totalsOf,
} from 'src/InvestmentAccountsView/holdingsModel';
import {formatMoney, formatQuantity, formatSigned, gainClass} from 'src/InvestmentAccountsView/money';
import {cn} from 'src/lib/utils';
import {Segmented} from 'src/Segmented';
import type {
  Holding,
  HoldingRow,
  HoldingSort,
  Sum,
} from 'src/InvestmentAccountsView/holdingsModel';

type View = 'security' | 'account';

const VIEWS: Array<{key: View; label: string}> = [
  {key: 'security', label: 'By security'},
  {key: 'account', label: 'By account'},
];

// Value is the default (the dropdown's "all"); the rest as options.
const SORTS: Array<{key: Exclude<HoldingSort, 'value'>; label: string}> = [
  {key: 'unrealized', label: 'Unrealized'},
  {key: 'return', label: 'Return'},
  {key: 'name', label: 'Name'},
];

/**
 * A sum of amounts: "—" when none is known; "≥" when only some are and the
 * rest can only add (see `partialClass` for when they could go either way).
 */
const formatSum = (value: Sum, currency: string): string =>
  value.known === 0 ? '—' : `${value.complete || !value.floor ? '' : '≥ '}${formatMoney(value.cents, currency)}`;

/** A partial sum that isn't a floor goes uncolored: it could be over or under. */
const partialClass = (value: Sum) => (!value.complete && !value.floor ? 'text-muted-foreground' : null);

const formatPrice = (price: string, currency: string): string => {
  const amount = Number(price).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 4});
  return currency === 'USD' ? `$${amount}` : `${amount} ${currency}`;
};


/** Holdings last observed this long ago say when. */
const STALE_DAYS = 14;

/** A share as a percent, one decimal (two under 1%). */
const formatShare = (ratio: number): string =>
  `${(ratio * 100).toLocaleString('en-US', {maximumFractionDigits: Math.abs(ratio) < 0.01 ? 2 : 1, minimumFractionDigits: 1})}%`;

/** A return as a signed percent. */
const formatReturn = (ratio: number): string => `${ratio < 0 ? '−' : '+'}${formatShare(Math.abs(ratio))}`;

// Every row is one grid with the same fixed column widths, so figures line
// up down the table. Columns appear as the card (a container) widens:
// value and unrealized always, basis next, then quantity and price.
const GRID =
  'grid grid-cols-[minmax(0,1fr)_5.75rem_5.75rem] items-center gap-x-3 @md:gap-x-4 @md:grid-cols-[minmax(0,1fr)_6.5rem_6.5rem] @2xl:grid-cols-[minmax(0,1fr)_6.5rem_6.5rem_6.5rem] @4xl:grid-cols-[minmax(0,1fr)_6rem_6rem_6.5rem_6.5rem_6.5rem]';

const WIDE = 'hidden text-right @4xl:block';
const MEDIUM = 'hidden text-right @2xl:block';

const HeaderRow = () => (
  <div
    className={cn(
      GRID,
      'text-muted-foreground border-border border-b px-3 pb-2 text-xs',
    )}
  >
    <span>Security</span>
    <span className={WIDE}>Quantity</span>
    <span className={WIDE}>Price</span>
    <span className="text-right">Value</span>
    <span className={MEDIUM}>Cost basis</span>
    <span className="text-right">Unrealized</span>
  </div>
);

/**
 * Stored securities the cash setting is offered for: ones a broker types
 * "other" or flags as cash-equivalent (where cash hides), and any already
 * set, to undo it. Never one that is cash by type.
 */
const settableOf = (holdings: Holding[]) => [
  ...new Map(
    holdings
      .filter(
        ({security}) =>
          security.source === 'plaid' &&
          security.securityType !== 'cash' &&
          (security.treatAsCash || security.isCash || security.securityType === 'other'),
      )
      .map(({security}) => [security._id, security]),
  ).values(),
];

/** The cash setting, for the given holdings' settable securities. */
const CashToggle = ({holdings}: {holdings: Holding[]}) => {
  // The Gains table refreshes itself on mount (cache-and-network).
  const [setTreatAsCash, {loading}] = useSetSecurityTreatAsCashMutation({
    refetchQueries: ['InvestmentHoldings'],
  });
  const securities = settableOf(holdings);
  if (securities.length === 0) return null;
  const treated = securities.every((security) => security.treatAsCash);
  return (
    <Button
      variant="ghost"
      className="gap-1.5"
      disabled={loading}
      onClick={() => {
        for (const security of securities) {
          void setTreatAsCash({variables: {securityId: security._id, treatAsCash: !treated}});
        }
      }}
    >
      <Coins className="size-3.5" />
      {treated ? "Don't count as cash" : 'Count as cash'}
    </Button>
  );
};

/**
 * A row's figures. Value carries its share of the portfolio (in its
 * currency) beneath; unrealized, its return on cost.
 */
const Cells = ({row, total, muted = false}: {row: HoldingRow; total: number; muted?: boolean}) => {
  const ratio = returnOf(row);
  return (
    <>
      <span className={cn(WIDE, 'tabular-nums')}>{row.quantity != null ? formatQuantity(row.quantity) : ''}</span>
      <span className={cn(WIDE, 'tabular-nums')}>{row.price != null ? formatPrice(row.price, row.currency) : ''}</span>
      <span className="flex flex-col items-end tabular-nums">
        <span className={cn(muted && 'text-muted-foreground', partialClass(row.value))}>
          {formatSum(row.value, row.currency)}
        </span>
        {row.value.known > 0 && total > 0 && (
          <span className="text-muted-foreground text-xs">{formatShare(row.value.cents / total)}</span>
        )}
      </span>
      <span className={cn(MEDIUM, 'tabular-nums')}>
        {row.cash ? '' : row.costBasis != null ? formatMoney(row.costBasis, row.currency) : '—'}
      </span>
      <span className={cn('flex flex-col items-end', gainClass(row.unrealized))}>
        <span>{row.cash ? '' : row.unrealized != null ? formatSigned(row.unrealized, row.currency) : '—'}</span>
        {ratio != null && <span className="text-xs">{formatReturn(ratio)}</span>}
      </span>
    </>
  );
};

/**
 * Everything about a row, for when the table is too narrow to show it:
 * its full name (or names: a cash holding's fund and account) first.
 */
const Details = ({row, total, heading = [row.name]}: {row: HoldingRow; total: number; heading?: string[]}) => {
  const ratio = returnOf(row);
  // The oldest quote the row's value rests on.
  const priceDate =
    row.holdings
      .flatMap((holding) => (holding.priceDate != null ? [holding.priceDate] : []))
      .sort()[0] ?? null;
  const items: Array<[string, string]> = [
    ...(row.quantity != null ? [['Quantity', formatQuantity(row.quantity)] as [string, string]] : []),
    ...(row.price != null
      ? [['Price', `${formatPrice(row.price, row.currency)}${priceDate ? ` · ${priceDate}` : ''}`] as [string, string]]
      : []),
    ['Value', formatSum(row.value, row.currency)],
    ...(row.cash ? [] : [['Cost basis', row.costBasis != null ? formatMoney(row.costBasis, row.currency) : '—'] as [string, string]]),
    ...(row.cash
      ? []
      : [
          [
            'Unrealized',
            row.unrealized != null
              ? `${formatSigned(row.unrealized, row.currency)}${ratio != null ? ` · ${formatReturn(ratio)}` : ''}`
              : '—',
          ] as [string, string],
        ]),
    ...(total > 0 && row.value.known > 0 ? [['Share', formatShare(row.value.cents / total)] as [string, string]] : []),
  ];
  return (
    <div className="flex flex-col gap-2 px-3 py-2 pl-8 text-xs">
      <div className="text-muted-foreground flex flex-col">
        {heading.map((line) => (
          <span key={line} className="break-words">
            {line}
          </span>
        ))}
      </div>
      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1 @md:grid-cols-[auto_minmax(0,1fr)_auto_minmax(0,1fr)]">
        {items.map(([label, value]) => (
          <React.Fragment key={label}>
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="tabular-nums">{value}</dd>
          </React.Fragment>
        ))}
      </dl>
    </div>
  );
};

const SecurityRow = ({
  row,
  total,
  accountName,
}: {
  row: HoldingRow;
  /** The portfolio's value in the row's currency, for its share. */
  total: number;
  accountName: (holding: Holding) => string;
}) => {
  const [open, setOpen] = React.useState(false);
  const accounts = rowsByAccountOf(row, accountName);
  // An investment's row offers the setting for the asset; cash's offers
  // undoing it per security, beside each (see the sub-rows).
  const settable = row.cash ? [] : row.holdings;
  const stale = today().diff(parseDate(row.asOf), 'days').days > STALE_DAYS;
  const name = (
    <span className="flex min-w-0 flex-col text-left">
      <span className="line-clamp-2 font-medium break-words">{row.name}</span>
      {(row.ticker != null || stale) && (
        <span className="text-muted-foreground text-xs">
          {/* Each part whole: a date never breaks across lines. */}
          {[row.ticker, stale ? `as of ${row.asOf}` : null]
            .filter((part) => part != null)
            .map((part, index) => (
              <React.Fragment key={part}>
                {index > 0 && ' · '}
                <span className="whitespace-nowrap">{part}</span>
              </React.Fragment>
            ))}
        </span>
      )}
    </span>
  );
  return (
    <div className="border-border border-b last:border-b-0">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((state) => !state)}
        className={cn(GRID, 'w-full px-3 py-2.5 text-left text-sm')}
      >
        <span className="flex min-w-0 items-center gap-1.5">
          {open ? (
            <ChevronDown className="text-muted-foreground size-4 shrink-0" />
          ) : (
            <ChevronRight className="text-muted-foreground size-4 shrink-0" />
          )}
          {name}
        </span>
        <Cells row={row} total={total} />
      </button>
      {open && (
        <div className="bg-muted/40 pb-1">
          <Details row={row} total={total} />
          {(accounts.length > 1 || row.cash) &&
            accounts.map((account) => <SubRow key={account.row.key} {...account} total={total} />)}
          {settableOf(settable).length > 0 && (
            <div className="px-3 pt-1 pl-7">
              <CashToggle holdings={settable} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

type SubRowProps = {
  row: HoldingRow;
  /** What it is: the account, or for cash, the fund. */
  label: string;
  /** For cash, the account it's in. */
  detail: string | null;
};

/**
 * A security row's holdings, one sub-row per account (an account's several
 * records of the asset summed); for cash, one per account and security.
 */
const rowsByAccountOf = (row: HoldingRow, accountName: (holding: Holding) => string): SubRowProps[] => {
  const groups = new Map<string, Holding[]>();
  for (const holding of row.holdings) {
    const account = accountKeyOf(holding);
    const key = row.cash ? `${account} ${holding.securityId}` : account;
    groups.set(key, [...(groups.get(key) ?? []), holding]);
  }
  // Built from the row's own members, which are already one asset.
  return [...groups.entries()]
    .map(([key, members]) => {
      const label = row.cash ? members[0].security.name : accountName(members[0]);
      return {row: rowOf(key, label, members), label, detail: row.cash ? accountName(members[0]) : null};
    })
    .sort((a, b) => b.row.value.cents - a.row.value.cents);
};

/**
 * One account's part of a row (or, under cash, one fund in one account),
 * itself expandable: on a narrow screen its names and figures are cut
 * short, so it opens to all of them, and for cash, to its cash setting.
 */
const SubRow = ({row, label, detail, total}: SubRowProps & {total: number}) => {
  const [open, setOpen] = React.useState(false);
  // Cash offers undoing the setting, per security.
  const settable = row.cash ? row.holdings.filter((holding) => holding.security.treatAsCash) : [];
  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((state) => !state)}
        className={cn(GRID, 'w-full px-3 py-1.5 text-left text-xs')}
      >
        <span className="flex min-w-0 items-center gap-1.5 pl-5">
          {open ? (
            <ChevronDown className="text-muted-foreground size-3.5 shrink-0" />
          ) : (
            <ChevronRight className="text-muted-foreground size-3.5 shrink-0" />
          )}
          <span className="flex min-w-0 flex-col">
            <span className="text-muted-foreground line-clamp-2 break-words">{label}</span>
            {detail != null && <span className="text-muted-foreground/70 line-clamp-2 break-words">{detail}</span>}
          </span>
        </span>
        <Cells row={row} total={total} muted />
      </button>
      {open && (
        <div className="pl-5">
          <Details row={row} total={total} heading={detail != null ? [label, detail] : [label]} />
          {settableOf(settable).length > 0 && (
            <div className="px-3 pb-1 pl-7">
              <CashToggle holdings={settable} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * The totals by tax treatment: taxable gains apart from sheltered ones
 * (tax-deferred, tax-free). Only when there's more than one to tell apart.
 */
const TreatmentBreakdown = ({holdings}: {holdings: Holding[]}) => {
  const rows = totalsByTreatment(holdings);
  if (new Set(rows.map((row) => row.treatment)).size < 2) return null;
  const currencies = new Set(rows.map((row) => row.currency)).size;
  return (
    <section aria-label="By tax treatment" className="@container">
      <div className="grid max-w-xl grid-cols-[minmax(0,1fr)_auto_auto] gap-x-6 gap-y-1 text-sm @md:grid-cols-[minmax(0,1fr)_auto_auto_auto]">
        <span className="text-muted-foreground text-xs">Tax treatment</span>
        <span className="text-muted-foreground text-right text-xs">Value</span>
        <span className="text-muted-foreground hidden text-right text-xs @md:block">Cost basis</span>
        <span className="text-muted-foreground text-right text-xs">Unrealized</span>
        {rows.map((row) => (
          <React.Fragment key={`${row.treatment} ${row.currency}`}>
            <span className="truncate">{currencies > 1 ? `${row.label} · ${row.currency}` : row.label}</span>
            <span className={cn('text-right tabular-nums', partialClass(row.value))}>
              {formatSum(row.value, row.currency)}
            </span>
            <span className={cn('hidden text-right tabular-nums @md:block', partialClass(row.costBasis))}>
              {formatSum(row.costBasis, row.currency)}
            </span>
            <span
              className={cn(
                'text-right tabular-nums',
                row.unrealized.complete ? gainClass(row.unrealized.cents) : 'text-muted-foreground',
              )}
            >
              {row.unrealized.known === 0 ? '—' : formatSigned(row.unrealized.cents, row.currency)}
            </span>
          </React.Fragment>
        ))}
      </div>
    </section>
  );
};

/**
 * The Holdings tab: every security held across accounts and company
 * stock, grouped by security (one identity across institutions) or by
 * account, valued today.
 */
export const InvestmentHoldingsView = () => {
  const [view, setView] = React.useState<View>('security');
  const [sort, setSort] = React.useState<HoldingSort>('value');
  const [accounts, setAccounts] = React.useState<string[]>([]);
  const [search, setSearch] = React.useState('');
  // Derived from everything a sync or a setting can change, so it always
  // refreshes on mount, showing the cached figures meanwhile.
  const {data, loading, error} = useInvestmentHoldingsQuery({fetchPolicy: 'cache-and-network'});
  const {data: accountsData} = useAllInvestmentAccountsQuery();

  const accountNames = React.useMemo(
    () => new Map((accountsData?.allInvestmentAccounts ?? []).map((account) => [account._id, account.name])),
    [accountsData],
  );
  const accountName = React.useCallback(
    (holding: Holding) =>
      holding.accountId != null
        ? (accountNames.get(holding.accountId) ?? 'Account')
        : holding.security.name.replace(/ options$/, ''),
    [accountNames],
  );

  if (loading && data == null) {
    return (
      <div className="flex items-center justify-center p-8">
        <span className="text-muted-foreground">Loading holdings...</span>
      </div>
    );
  }
  if (data == null && error != null) {
    return (
      <div className="text-muted-foreground p-8 text-center text-sm">
        Couldn&apos;t load holdings.
      </div>
    );
  }
  const holdings = data?.investmentHoldings ?? [];
  if (holdings.length === 0) {
    return (
      <div className="text-muted-foreground p-8 text-center text-sm">No holdings.</div>
    );
  }

  // The totals are of what's shown; a row's share is of the whole portfolio.
  // Which asset a holding is, from every holding: two records of one asset
  // may join only through a third the filter leaves out.
  const assetOf = assetsOf(holdings);
  const shown = filterHoldings(holdings, {accounts, search}, accountName, assetOf);
  const totals = totalsOf(shown);
  const portfolio = totalsOf(holdings);
  // A share needs the whole: with any holding unvalued, every known one's
  // would be overstated, so none is shown.
  const totalOf = (currency: string) => {
    const value = portfolio.find((total) => total.currency === currency)?.value;
    return value?.complete ? value.cents : 0;
  };
  const accountOptions = [
    ...new Map(holdings.map((holding) => [accountKeyOf(holding), accountName(holding)])).entries(),
  ]
    .map(([key, label]) => ({key, label, count: holdings.filter((holding) => accountKeyOf(holding) === key).length}))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <div className="flex flex-1 flex-col overflow-y-auto">
      <div className="flex flex-col gap-4 p-4 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-col gap-2">
            {totals.map((total) => (
              <dl key={total.currency} className="flex flex-wrap gap-x-8 gap-y-2">
                <div>
                  <dt className="text-muted-foreground text-xs">Value</dt>
                  <dd className={cn('text-2xl font-semibold tabular-nums', partialClass(total.value))}>
                    {formatSum(total.value, total.currency)}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground text-xs">Cost basis</dt>
                  <dd className={cn('text-2xl font-semibold tabular-nums', partialClass(total.costBasis))}>
                    {formatSum(total.costBasis, total.currency)}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground text-xs">Unrealized</dt>
                  {/* A partial gain can be negative, so it can't read as a
                      floor: it goes uncolored instead. */}
                  <dd
                    className={cn(
                      'text-2xl font-semibold',
                      total.unrealized.complete
                        ? gainClass(total.unrealized.cents)
                        : 'text-muted-foreground tabular-nums',
                    )}
                  >
                    {total.unrealized.known === 0 ? '—' : formatSigned(total.unrealized.cents, total.currency)}
                  </dd>
                </div>
              </dl>
            ))}
          </div>
          <Segmented label="Group holdings" options={VIEWS} value={view} onChange={setView} />
        </div>

        <TreatmentBreakdown holdings={shown} />

        <div className="flex flex-wrap items-center gap-2">
          {/* On a phone the search takes its own line, the menus the next. */}
          <div className="relative w-full sm:w-auto sm:min-w-40 sm:flex-1">
            <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
            <input
              type="search"
              aria-label="Search holdings"
              placeholder="Search holdings..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="border-border bg-background placeholder:text-muted-foreground focus:ring-primary w-full rounded-md border py-2 pr-3 pl-9 text-sm focus:ring-2 focus:outline-none"
            />
          </div>
          {accountOptions.length > 1 && (
            <FilterDropdown
              label="Accounts"
              allLabel="All accounts"
              allCount={holdings.length}
              multiple
              value={accounts}
              onChange={setAccounts}
              options={accountOptions}
            />
          )}
          <FilterDropdown
            label="Sort"
            allLabel="Value"
            value={sort === 'value' ? 'all' : sort}
            onChange={(key) => setSort(SORTS.find((option) => option.key === key)?.key ?? 'value')}
            options={SORTS}
          />
        </div>

        {shown.length === 0 ? (
          <div className="text-muted-foreground p-8 text-center text-sm">No matching holdings.</div>
        ) : view === 'security' ? (
          <div className="border-border bg-card @container rounded-lg border pt-2">
            <HeaderRow />
            {sortRows(rowsBySecurity(shown, assetOf), sort).map((row) => (
              <SecurityRow key={row.key} row={row} total={totalOf(row.currency)} accountName={accountName} />
            ))}
          </div>
        ) : (
          groupsByAccount(shown, accountName, assetOf).map((group) => (
            <section key={group.key} className="border-border bg-card @container rounded-lg border pt-2">
              <div className="flex items-baseline justify-between px-3 pb-2">
                <h2 className="truncate text-sm font-semibold">{group.name}</h2>
                <span className="text-muted-foreground text-sm tabular-nums">
                  {group.totals
                    .map((total) =>
                      formatSum(total.value, total.currency)
                    )
                    .join(' · ')}
                </span>
              </div>
              <HeaderRow />
              {sortRows(group.rows, sort).map((row) => (
                <SecurityRow key={row.key} row={row} total={totalOf(row.currency)} accountName={accountName} />
              ))}
            </section>
          ))
        )}
      </div>
    </div>
  );
};
